import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { Helmet } from 'react-helmet';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { PageSection } from 'apps/sigma2-ui/src/components/PageSection';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useSavedAnalysis } from 'apps/sigma2-ui/src/filters-v2/lib/useSavedAnalysis';
import { SavedAnalysisCard } from 'apps/sigma2-ui/src/pages/SavedAnalysesPage/SavedAnalysisCard';
import { SectionGradient } from 'apps/sigma2-ui/src/pages/FocusAreaPageComponent';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { NoSavedAnalyses } from 'apps/sigma2-ui/src/pages/SavedAnalysesPage/NoSavedAnalyses';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { SelectAllButton } from 'apps/sigma2-ui/src/filters-v2/components/SelectAllButton';
import { ClearAllButton } from 'apps/sigma2-ui/src/filters-v2/components/ClearAllButton';
import { ExportButton } from 'apps/sigma2-ui/src/components/ExportButton';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useReports } from 'apps/sigma2-ui/src/reports/hooks/useReports';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { useIsUserAnalysisEnabled } from 'apps/sigma2-ui/src/components/ui/useUserAnalysis';
import { isEntitledToReports } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSavedAnalysis } from 'libs/end-to-end/src/group-entitlements';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { GenerateReportRequest } from 'libs/end-to-end/src/generate-report';

export const SavedAnalysesPage = () => {
  const { myObservation } = useObservation();

  const language = useLanguage();

  useAuthenticatedPageRedirect();

  const navigate = useNavigateSafe();

  const showSavedAnalyses = myObservation?.showSavedAnalysesPage;

  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation();

  const savedAnalysesEnabled = useMemo(() => {
    return (
      (myObservation?.showSavedAnalysesPage ?? false) &&
      isEntitledToSavedAnalysis({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation?.features, myObservation?.showSavedAnalysesPage]);

  const analytics = useAnalytics();

  useEffect(() => {
    analytics.trackEvent({
      eventName: 'Saved Page Viewed',
      eventProperties: {},
    });
  }, [analytics]);

  if (showSavedAnalyses === false) {
    navigate('/overview');
  }

  return (
    <MainLayout showBackToTop={isLoadingStill}>
      <Helmet>
        <title>{documentTitle('Saved analyses')}</title>
      </Helmet>

      {savedAnalysesEnabled && (
        <SectionGradient>
          <PageSection>
            <div className="mt-0">
              <Spacing direction="vertical" size="md">
                <div className="mt-0">
                  <S2Typography
                    variant="headline-3"
                    translatedContent={language.saved()}
                  />
                </div>
              </Spacing>

              <TabsRadioGroup
                currValue="All Saved"
                values={[{ tabValue: 'All Saved' }]}
                onChange={() => {}}
                minWidth="auto"
                gap="md"
                underlineBgClass="bg-secondary"
                renderTab={() => (
                  <S2Typography
                    variant="headline-6-alt"
                    translatedContent={language.allSaved()}
                    dangerouslyOverrideClassNameFn={replaceTextColor(
                      'text-secondary',
                    )}
                  />
                )}
              />
            </div>
          </PageSection>

          <SavedAnalysesView
            isLoadingStill={isLoadingStill}
            failedToLoadObservation={failedToLoadObservation}
          />
        </SectionGradient>
      )}
    </MainLayout>
  );
};

const SavedAnalysesView = ({
  isLoadingStill,
  failedToLoadObservation,
}: {
  isLoadingStill: boolean;
  failedToLoadObservation: boolean;
}) => {
  const { savedAnalysesList } = useSavedAnalysis();
  const { myObservation } = useObservation();
  const { exportReport } = useReports();

  const isPageStillLoading = isLoadingStill || savedAnalysesList === null;

  const allSavedAnalysisIds = useMemo(() => {
    return savedAnalysesList?.map(
      (savedAnalysis) => savedAnalysis.savedAnalysisId,
    );
  }, [savedAnalysesList]);

  const isUserAnalysisEnabled = useIsUserAnalysisEnabled();
  const [checkedAnalysisIds, setCheckedAnalysisIds] = useState<string[]>([]);
  const [blockExportingButton, setBlockExportingButton] = useState(false);

  const showDownloadMultiAnalysisButton = useMemo(
    () =>
      myObservation?.showDownloadMultiAnalysisButton &&
      isUserAnalysisEnabled &&
      isEntitledToReports({
        features: myObservation?.features ?? [],
      }),
    [
      myObservation?.showDownloadMultiAnalysisButton,
      myObservation?.features,
      isUserAnalysisEnabled,
    ],
  );

  const anyAreSelected = useMemo(() => {
    return checkedAnalysisIds.length > 0;
  }, [checkedAnalysisIds.length]);

  const allAreSelected = useMemo(() => {
    return (
      allSavedAnalysisIds?.length !== undefined &&
      checkedAnalysisIds.length === allSavedAnalysisIds.length
    );
  }, [allSavedAnalysisIds, checkedAnalysisIds.length]);

  const onSelectAll = useCallback(() => {
    if (allAreSelected) {
      setCheckedAnalysisIds([]);
    } else {
      setCheckedAnalysisIds(allSavedAnalysisIds || []);
    }
  }, [allAreSelected, allSavedAnalysisIds]);

  const onClearAll = useCallback(() => {
    setCheckedAnalysisIds([]);
  }, [setCheckedAnalysisIds]);

  const generateReportRequest: GenerateReportRequest | undefined =
    useMemo(() => {
      if (!savedAnalysesList) {
        return;
      }

      return {
        charts: savedAnalysesList
          // Ensure that we maintain the same order as savedAnalysesList, which will be important because we plan to support re-ordering saved analysis.
          .filter((savedAnalysis) =>
            checkedAnalysisIds.includes(savedAnalysis.savedAnalysisId),
          )
          .map((savedAnalysis) => savedAnalysis.customVizRequest)
          .filter((request): request is CustomVizRequest => request !== null)
          .map((customVizRequest) => ({ customVizRequest })),
      };
    }, [checkedAnalysisIds, savedAnalysesList]);

  const onExport = useCallback(() => {
    if (!generateReportRequest) {
      return;
    }

    setBlockExportingButton(true);

    exportReport(generateReportRequest, () => {
      setTimeout(() => {
        setBlockExportingButton(false);
      }, 3000);
    });
  }, [generateReportRequest, exportReport]);

  return (
    <div className="mt-6">
      <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />

      <PageSection>
        <ObservationLoadingAndFailureIndicators
          isLoadingStill={isPageStillLoading}
          failedToLoadObservation={failedToLoadObservation}
          offset={307}
        />
        {!isPageStillLoading &&
          (savedAnalysesList?.length === 0 ? (
            <NoSavedAnalyses />
          ) : (
            <Spacing direction="vertical" size="xl">
              {showDownloadMultiAnalysisButton && (
                <HorizontalFilterBar
                  items={[
                    [
                      {
                        key: 'selectAll',
                        content: () => (
                          <SelectAllButton
                            onClick={onSelectAll}
                            disabled={false}
                            filled={allAreSelected}
                          />
                        ),
                        menu: () => <></>,
                      },
                      ...(anyAreSelected
                        ? [
                            {
                              key: 'clearAll',
                              content: () => (
                                <ClearAllButton
                                  onClick={onClearAll}
                                  disabled={false}
                                />
                              ),
                              menu: () => <></>,
                            },
                          ]
                        : []),
                    ],
                    [
                      {
                        key: 'export',
                        content: () => (
                          <ExportButton
                            disabled={!anyAreSelected || blockExportingButton}
                            amount={checkedAnalysisIds.length}
                            exporting={blockExportingButton}
                            onClick={onExport}
                          />
                        ),
                        menu: () => <></>,
                      },
                    ],
                  ]}
                />
              )}
              <div
                data-testid="saved-analyses-card-container"
                className="grid grid-cols-3 gap-4"
              >
                {savedAnalysesList.map((savedAnalysis) => (
                  <SavedAnalysisCard
                    key={savedAnalysis.savedAnalysisId}
                    savedAnalysis={savedAnalysis}
                    isChecked={checkedAnalysisIds.includes(
                      savedAnalysis.savedAnalysisId,
                    )}
                    onCheck={() => {
                      setCheckedAnalysisIds((prev) =>
                        prev.includes(savedAnalysis.savedAnalysisId)
                          ? prev.filter(
                              (id) => id !== savedAnalysis.savedAnalysisId,
                            )
                          : [...prev, savedAnalysis.savedAnalysisId],
                      );
                    }}
                  />
                ))}
              </div>
            </Spacing>
          ))}
      </PageSection>
    </div>
  );
};
